import {
  useCallback, useEffect, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { MAPTIVE_SUPPORT_EMAIL } from '~/_shared/constants/maptive';
import {
  DATA_ROUTE, MAP_ROUTE,
} from '~/_shared/constants/routes';
import { getEmailDraftHref } from '~/_shared/utils/email/email.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { isRouteInRoutes } from '~/_shared/utils/tests/router.helpers';
import {
  limitNotificationsAddItem, limitNotificationsRemoveItem,
} from '~/store/frontendState/limitNotifications/limitNotifications.actionCreators';
import { restartGeocoding } from '~/store/geocoding/geocoding.repository';
import { useMapInfoDataSelector } from '~/store/mapInfo/mapInfo.selectors';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { usePrimarySpreadsheetId } from '~/store/selectors/usePrimarySpreadsheetId';
import {
  useUserDataEmailSelector, useUserDataSelector,
} from '~/store/userData/userData.selectors';

const PUBLIC_VIEWS_LIMIT_KEY = 'PUBLIC_VIEWS_LIMIT_KEY';
const GEOCODING_LIMIT_KEY = 'GEOCODING_LIMIT_KEY';
export const MAP_GEOCODING_LIMIT_KEY = 'MAP_GEOCODING_LIMIT_KEY';

export const useLimitsChecker = () => {
  const spreadsheetId = usePrimarySpreadsheetId();
  const clientEmail = useUserDataEmailSelector();
  const currentUser = useUserDataSelector();
  const clientId = useClientIdSelector();
  const location = useLocation();
  const mapInfo = useMapInfoDataSelector();
  const dispatch = useDispatch();
  const [t] = useTranslation();

  const isUserOverRealGeocodesLimit = currentUser.usage.realGeocodes >= currentUser.limits.realGeocodes;

  const contactSupportEmailLink = useMemo(() => {
    return getEmailDraftHref(
      MAPTIVE_SUPPORT_EMAIL,
      t('Account Limit Increase'),
      t('I would like to increase limits for my account {{clientEmail}}', { clientEmail })
    );
  }, [clientEmail, t]);

  const onRestartGeocodingClick = useCallback(() => {
    if (!clientId || !spreadsheetId) {
      return;
    }
    restartGeocoding(clientId, spreadsheetId);
    dispatch(limitNotificationsRemoveItem(MAP_GEOCODING_LIMIT_KEY));
  }, [clientId, dispatch, spreadsheetId]);

  // verify public map view
  useEffect(() => {
    if (!currentUser.isReady) {
      return;
    }

    if (currentUser.usage.publicMapViews >= currentUser.limits.mapView) {
      dispatch(limitNotificationsAddItem({
        key: PUBLIC_VIEWS_LIMIT_KEY,
        type: 'warning',
        heading: t('Public Map View Limit Reached'),
        text: t('limits.publicMapViewsLimitExceeded'),
        button: {
          text: t('Contact Support'),
          href: contactSupportEmailLink,
        },
      }));
    }
  }, [contactSupportEmailLink, currentUser.isReady, currentUser.limits.mapView, currentUser.usage.publicMapViews, dispatch, t]);

  // verify real geocodes limits
  useEffect(() => {
    if (!currentUser.isReady) {
      return;
    }

    if (isUserOverRealGeocodesLimit) {
      dispatch(limitNotificationsAddItem({
        key: GEOCODING_LIMIT_KEY,
        type: 'warning',
        heading: t('Location Mapping Limit Reached'),
        text: t('limits.geocodingLimitExceeded'),
        button: {
          text: t('Contact Support'),
          href: contactSupportEmailLink,
        },
      }));
    }
  }, [contactSupportEmailLink, currentUser.isReady, dispatch, isUserOverRealGeocodesLimit, t]);

  // verify map page geocoding
  useEffect(() => {
    if (!currentUser.isReady) {
      return;
    }

    if (!isRouteInRoutes(location.pathname, [MAP_ROUTE, DATA_ROUTE]) || !mapInfo) {
      return;
    }

    const geocodingOverLimit = mapInfo.spreadsheets.find(
      spreadsheet => spreadsheet.geocoding &&
        spreadsheet.geocoding.is_required &&
        spreadsheet.geocoding.limit_reached
    )?.geocoding;
    if (!geocodingOverLimit || geocodingOverLimit.bad_data === 0) {
      return;
    }

    if (isUserOverRealGeocodesLimit) {
      dispatch(limitNotificationsAddItem({
        key: MAP_GEOCODING_LIMIT_KEY,
        type: 'info',
        heading: t('Map Processing Paused'),
        text: t('limits.locationsLimitExceededIncreaseLimit', { count: geocodingOverLimit.bad_data }),
        button: {
          text: t('Contact Support'),
          href: contactSupportEmailLink,
        },
      }));
    }
    else {
      dispatch(limitNotificationsAddItem({
        key: MAP_GEOCODING_LIMIT_KEY,
        type: 'info',
        heading: t('Map Processing Paused'),
        text: t('limits.locationsLimitExceeded', { count: geocodingOverLimit.bad_data }),
        button: {
          text: t('Process Remaining Locations'),
          onClick: onRestartGeocodingClick,
        },
      }));
    }
  }, [contactSupportEmailLink, currentUser.isReady, dispatch, isUserOverRealGeocodesLimit, location.pathname, mapInfo,
    onRestartGeocodingClick, t]);
};
